import { useEffect, useRef, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Upload, Loader2, Check, AlertTriangle, FileSpreadsheet } from 'lucide-react';
import { db, functions } from '../../config/firebase';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import type { CatalogPreview, PlannedCatalogRow, StockLocation } from '../../types/stock';

const ACTION_LABELS: Record<PlannedCatalogRow['action'], string> = {
  create_product: 'New product',
  add_size: 'New size',
  update_stock: 'Update stock',
  remap_sku: 'Re-map SKU',
};

const ACTION_STYLES: Record<PlannedCatalogRow['action'], string> = {
  create_product: 'bg-green-50 text-green-700',
  add_size: 'bg-blue-50 text-blue-700',
  update_stock: 'bg-gray-100 text-gray-700',
  remap_sku: 'bg-amber-50 text-amber-700',
};

const SAMPLE = 'sku,name,size,quantity,price\nSILK-MIDI-S,Silk Midi Dress,S,4,248\nSILK-MIDI-M,Silk Midi Dress,M,2,248';

/**
 * Catalog import for boutiques that are not on Shopify. Every external sale
 * FASH can act on has to name a SKU, so this is the step that makes Square,
 * Stripe and Partner API reporting possible at all.
 */
export function CatalogImport({ businessId }: { businessId: string }) {
  const [locations, setLocations] = useState<StockLocation[]>([]);
  const [locationId, setLocationId] = useState('');
  const [text, setText] = useState('');
  const [preview, setPreview] = useState<CatalogPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'stock_locations'), where('businessId', '==', businessId)),
      (snap) => {
        const rows = snap.docs
          .map((d) => d.data() as StockLocation)
          .filter((l) => l.active !== false)
          .sort((a, b) => Number(!!b.isDefault) - Number(!!a.isDefault));
        setLocations(rows);
        setLocationId((current) => current || rows[0]?.locationId || '');
      },
    );
    return unsub;
  }, [businessId]);

  const selected = locations.find((l) => l.locationId === locationId);

  const onFile = async (file: File) => {
    setError(null);
    setPreview(null);
    setResult(null);
    setText(await file.text());
  };

  const runPreview = async () => {
    if (!text.trim()) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const fn = httpsCallable(functions, 'previewCatalogImport');
      const res = await fn({ businessId, locationId: locationId || undefined, text });
      setPreview(res.data as CatalogPreview);
    } catch (err: any) {
      setError(err?.message || 'Could not read that file');
    }
    setBusy(false);
  };

  const commit = async () => {
    if (!preview?.rows.length) return;
    setBusy(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'commitCatalog');
      const res: any = await fn({ businessId, locationId: preview.locationId, text });
      const { created, updated, skipped, errors } = res.data;
      setResult(
        `${created} row(s) created, ${updated} updated${skipped ? `, ${skipped} skipped` : ''}.` +
          (errors?.length ? ` First problem: ${errors[0]}` : ''),
      );
      setPreview(null);
      setText('');
    } catch (err: any) {
      setError(err?.message || 'Import failed');
    }
    setBusy(false);
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div className="flex items-start gap-3">
          <FileSpreadsheet className="h-5 w-5 text-primary mt-0.5" />
          <div>
            <p className="text-sm font-medium text-heading">Import your catalog</p>
            <p className="text-xs text-body">
              For stores that are not on Shopify. Paste or upload a product list with a SKU column —
              the SKU is how FASH recognises a sale that happens on your own site, Square or Stripe.
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-xs font-medium text-heading uppercase tracking-wider">
            Stock lands at
          </label>
          <select
            className="w-full text-sm border rounded-md h-9 px-2"
            value={locationId}
            onChange={(e) => {
              setLocationId(e.target.value);
              setPreview(null);
            }}
          >
            {locations.map((l) => (
              <option key={l.locationId} value={l.locationId}>
                {l.name}
              </option>
            ))}
          </select>
          {selected?.writerSource && (
            <p className="text-[11px] text-amber-700">
              An external system already reports stock at this location. An import will set the
              counts once; after that the external system stays in charge.
            </p>
          )}
        </div>

        <textarea
          className="w-full text-xs font-mono border rounded-md p-2 h-32"
          placeholder={SAMPLE}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setPreview(null);
          }}
        />

        <div className="flex flex-wrap gap-2">
          <input
            ref={fileInput}
            type="file"
            accept=".csv,.tsv,.txt"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void onFile(file);
            }}
          />
          <Button type="button" size="sm" variant="outline" onClick={() => fileInput.current?.click()}>
            <Upload className="h-3.5 w-3.5 mr-1" />
            Choose CSV
          </Button>
          <Button type="button" size="sm" disabled={busy || !text.trim()} onClick={runPreview}>
            {busy && !preview ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
            Preview
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setText(SAMPLE)}>
            Use sample
          </Button>
        </div>

        {error && (
          <p className="text-xs text-red-600 flex items-start gap-1">
            <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" />
            {error}
          </p>
        )}
        {result && (
          <p className="text-xs text-green-700 flex items-start gap-1">
            <Check className="h-3.5 w-3.5 mt-px shrink-0" />
            {result}
          </p>
        )}

        {preview && (
          <div className="space-y-3 border-t pt-3">
            {preview.errors.length > 0 && (
              <div className="rounded-lg bg-amber-50 p-2 space-y-1">
                {preview.errors.slice(0, 6).map((e) => (
                  <p key={e} className="text-[11px] text-amber-800">
                    {e}
                  </p>
                ))}
                {preview.errors.length > 6 && (
                  <p className="text-[11px] text-amber-800">
                    …and {preview.errors.length - 6} more.
                  </p>
                )}
              </div>
            )}

            {preview.unmappedHeaders.length > 0 && (
              <p className="text-[11px] text-body">
                Ignored columns: {preview.unmappedHeaders.join(', ')}
              </p>
            )}

            {preview.rows.length > 0 && (
              <>
                <p className="text-xs text-body">
                  {preview.summary.total} row(s): {preview.summary.createProducts} new product(s),{' '}
                  {preview.summary.addSizes} new size(s), {preview.summary.updateStock} stock update(s)
                  {preview.summary.remapSku ? `, ${preview.summary.remapSku} SKU re-map(s)` : ''}.
                </p>

                <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-100 divide-y">
                  {preview.rows.map((row) => (
                    <div key={row.sku} className="flex items-center gap-2 px-2 py-1.5 text-xs">
                      <span
                        className={`px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${ACTION_STYLES[row.action]}`}
                      >
                        {ACTION_LABELS[row.action]}
                      </span>
                      <span className="font-mono text-[11px] text-body shrink-0">{row.sku}</span>
                      <span className="truncate flex-1 text-heading">
                        {row.productName} · {row.sizeLabel}
                      </span>
                      <span className="shrink-0 text-body">
                        {row.currentQuantity !== null && row.currentQuantity !== row.quantity
                          ? `${row.currentQuantity} → ${row.quantity}`
                          : row.quantity}
                      </span>
                    </div>
                  ))}
                </div>

                {preview.rows.some((r) => r.note) && (
                  <div className="space-y-1">
                    {preview.rows
                      .filter((r) => r.note)
                      .slice(0, 4)
                      .map((r) => (
                        <p key={r.sku} className="text-[11px] text-body">
                          <span className="font-mono">{r.sku}</span>: {r.note}
                        </p>
                      ))}
                  </div>
                )}

                <Button type="button" size="sm" disabled={busy} onClick={commit}>
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />}
                  Import {preview.rows.length} row(s)
                </Button>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
